// src/components/hero/index.js
import React, { Component } from 'react'
import values from 'lodash.values'
import classnames from 'classnames'
import HeroImage from './hero-image'
import HeroVideo from './hero-video'
import HeroIntro from './hero-intro'
import HeroSpinner from './hero-spinner'
import HeroTooltip from './hero-tooltip'
import { selectBestVimeoSource, selectBestVimeoPoster, httpsify } from '../../lib/vimeo_data_util'

import './hero.css'

class Hero extends Component {

  constructor(props) {
    super(props)
    this.state = {
      transition: false,
      textInTransition: false,
      textOutTransition: false,
      currentTitle: null,
      currentSubtitle: null,
      index: 0,
      icon: false,
      slidesReadyState: {},
      slidesReady: false,
      introTimerDone: false,
      revealIntro: false,
      spinnerHash: 0,
    }

    this.duration = 5000
    this.transitionDuration = 400
    this.textInStart = 1
    this.textOutStart = 4900
  }

  componentDidMount() {
    this.requestSlides(true)
    
    // minimum time required before reveal even 
    // if it loads faster..
    setTimeout(this.handleIntroTimerDone, 2000)
  }

  componentWillReceiveProps(nextProps) {
    if (nextProps.slides !== this.props.slides) {
      this.setState({ slidesReadyState: {} })
    }
    if ((this.props.st !== nextProps.st) && (nextProps.st > 0)) {
      this.setState({ icon: false })
    }
  }

  componentDidUpdate(prevProps) {
    const nextProps = this.props
    this.updateOnActive(prevProps, nextProps)
    this.updateOnSlides(prevProps, nextProps)
    this.updateOnInViewport(prevProps, nextProps)
    this.updateOnPreventScroll(prevProps, nextProps)
  }

  updateOnPreventScroll(prevProps, nextProps) {
    const { preventScroll: nextPreventScroll } = nextProps
    const { preventScroll: prevPreventScroll } = prevProps

    if (nextPreventScroll !== prevPreventScroll) {
      if (!nextProps.slides) {
        return false
      }
      return nextPreventScroll ? this.stopAnimation() : this.startAnimation()
    }
  }

  updateOnInViewport(prevProps, nextProps) {
    const { inViewport: nextInViewport } = nextProps
    const { inViewport: prevInViewport } = prevProps

    if (nextInViewport !== prevInViewport) {
      if (nextProps.preventScroll) {
        return false
      }
      return nextInViewport ? this.startAnimation(true) : this.stopAnimation()
    }
  }

  updateOnSlides(prevProps, nextProps) {
    const { slides: nextSlides, inViewport: nextInViewport } = nextProps
    const { slides: prevSlides } = prevProps

    if (nextSlides !== prevSlides) {
      if (nextSlides && nextInViewport) {
        if (nextProps.preventScroll) {
          return false
        }
        this.startAnimation()
      } else this.stopAnimation()
    }
  }

  updateOnActive(prevProps, nextProps) {

    const {
      inViewport: nextInViewport,
      active: nextActive,
      slides: nextSlides } = nextProps
    const { active: prevActive } = prevProps

    if (nextActive !== prevActive) {
      if (nextActive) {
        if (!nextSlides) {
          this.requestSlides(true)
        } else {
          if (nextInViewport) {
            if (nextProps.preventScroll) {
              return false
            }
            this.startAnimation()
          }
        }
      } else {
        this.stopAnimation()
      }
    }
  }

  requestSlides(overrideActive) {
    const { slides, onStartup, active, fetching } = this.props

    if (!slides && (active || overrideActive) && !fetching) {
      onStartup && onStartup()
    }
  }

  componentWillUnmount() {
    this.stopAnimation()
  }

  shouldComponentUpdate(nextProps, nextState) {
    if (nextState.slidesReady !== this.props.slidesReady) return true
    if (nextProps.st !== this.props.st) return true
    if (nextProps.preventScroll !== this.props.preventScroll) return true
    if (nextProps.inViewport !== this.props.inViewport) return true
    if (nextProps.active !== this.props.active) return true
    if (nextProps.slides !== this.props.slides) return true
    if (nextState.index !== this.state.index) return true
    if (nextState.transition !== this.state.transition) return true
    if (nextState.textInTransition !== this.state.textInTransition) return true
    if (nextState.textOutTransition !== this.state.textOutTransition) return true
    return false
  }

  stopAnimation() {
    if (!this.ticking) return
    this.ticking = false
    
    this.setState({ changeSlidePending: false })

    if (this.slideTimeout) {
      clearTimeout(this.slideTimeout)
      this.slideTimeout = null
    }
    if (this.transitionTimeout) {
      clearTimeout(this.transitionTimeout)
      this.transitionTimeout = null
    }
    if (this.textInTransitionTimeout) {
      clearTimeout(this.textInTransitionTimeout)
      this.textInTransitionTimeout = null
    }
    if (this.textOutTransitionTimeout) {
      clearTimeout(this.textOutTransitionTimeout)
      this.textOutTransitionTimeout = null
    }

  }

  startAnimation(repeat) {

    if (this.ticking || !this.props.active) {
      return
    }
    
    this.setState(state => ({ 
      changeSlidePending: false, 
      spinnerHash: Math.random().toString(16).split('.')[1]
    }))

    const initialDelay = 0 // 4000 // repeat ? 0 : 4000

    this.ticking = true
    this.slideTimeout = setTimeout(this.changeSlide, this.duration + initialDelay)
    this.transitionTimeout = setTimeout(this.transitionSlide, (this.duration - this.transitionDuration) + initialDelay)
    this.textInTransitionTimeout = setTimeout(this.transitionInText, (1 + initialDelay))
    this.textOutTransitionTimeout = setTimeout(this.transitionOutText, (4000 + initialDelay))

  }

  transitionOutText = () => {
    this.setState({
      textOutTransition: true,
      textInTransition: false
    })
  }

  transitionInText = () => {
    this.setState({
      textInTransition: true
    })
  }

  transitionSlide = () => {
    this.setState({
      transition: true
    })
  }

  nextIndex(index) {
    const { slides } = this.props

    if (slides === null || typeof slides === 'undefined') {
      console.error('Hero slides are null, cannot go to next index')
      return index
    }

    const newIndex = (index >= (slides.size - 1)) ? 0 : ++index
    return newIndex
  }
  
  /**
   * invoked when an image is mounted, we just add it to a map
   * and mark it as loading
   */
  handleSlidePrepare = (src) => {
    this.setState(state => ({
      slidesReady: false,
      slidesReadyState: { ...state.slidesReadyState, [`${src}`]: false }
    }))
  }
  /**
   * Invoked when an image is loaded, we use this to check if 
   * the first image set is loaded in order to reveal  the intro
   */
  handleSlideLoad = (src) => {
    this.setState(state => ({
      slidesReadyState: { ...state.slidesReadyState, [`${src}`]: true }
    }), () => {
      // no need to perform the check if it's been marked as OK
      if (this.state.slidesReady || !this.state.slidesReadyState ) { return }
      // look if there's at least one image yet to load
        
      const allLoaded = (undefined === values(this.state.slidesReadyState)
        .find(image => image === false))  
      // ...if so, skip and come back later 
      if(!allLoaded) { return }
      this.setState(state => ({ slidesReady: true }), () => {
        // since all slides are loaded, we can now check if 
        // all the preconditions are cleared to reveal the intro
        this.checkReadyState()
      })
      
    })
  }
  
  /**
   * Timer ensuring the intro doesn't reveal to quick
   * on fast connections.
   */
  handleIntroTimerDone = () => {
    this.setState({ introTimerDone: true  }, this.checkReadyState)
  }
  
  checkReadyState = () => {
    if (!this.state.revealIntro && this.getReadyState()) {
      this.setState({ revealIntro: true })
    }
    if (this.state.revealIntro && this.state.changeSlidePending) {
      this.changeSlide()
    }
  }
  
  getReadyState = () => {
    return (this.state.introTimerDone && this.state.slidesReady)
  }

  changeSlide = () => {
    
    const index = this.nextIndex(this.state.index)
    
    // TODO:  should check if next frame is actually loaded 
    //        before incrementing
    
    const ready = this.getReadyState()
    
    if ( !ready ) { 
      this.setState({ changeSlidePending: true })
      return 
    }
    
    this.setState({
      index,
      changeSlidePending: false,
      transition: false,
      textInTransition: false,
      textOutTransition: false
    })

    this.ticking = false
    this.startAnimation(true)
  }

  // Slides fade out to reveal slide beneath
  // So we have to re-order after transition
  // To ensure there visible slide is always
  // at the top of the stack
  renderSlides(slides, vw) {
    const { transition } = this.state

    return slides.map((slide, index) => {

      const isActive = (index === 1)

      const slideClassNames = classnames({
        'hero__slide--active': isActive,
        'hero__slide--transition': transition && isActive
      }, 'hero__slide')
      
      if ( !slide.media || !slide.media.first()  ) { return null }
      
      const isTwin = slide.media.size > 1
      const imgClassNames = classnames({
        'hero__image--twin': isTwin
      }, 'hero__image')
      
      const media = slide.media && slide.media.map((item, index) => {
        const type = item.get('type') || 'image'
        if (type === 'video') {
          const video = item.get('hero_video').toJS()
          const mediaWidth = vw
          const videoSrc = selectBestVimeoSource(video, mediaWidth)
          videoSrc.link = videoSrc.link_secure || httpsify(videoSrc.link)
          const videoPosterSrc = selectBestVimeoPoster(video, mediaWidth)
          videoPosterSrc.link = httpsify(videoPosterSrc.link)
          return { 
            type: 'video',
            key: videoSrc.link,
            videoSrc, 
            videoPosterSrc,
          }
        } else {
          return {
            type: 'image',
            key: item.getIn(['hero_image', 'sizes', 'medium']),
            src: item.getIn(['hero_image', 'sizes', isTwin ? 'medium' : 'xlarge']),
            name: item.getIn(['hero_image', 'name']),
            width: item.getIn(['hero_image', 'width']),
            height: item.getIn(['hero_image', 'height']),
          }
        }
      })
      
      const { key } = media.first()
      
      return (
        <div className={slideClassNames} key={ key }>
          <div className="hero__image-container">
            {media && media.map((item, index) => (
              item.type === 'image' 
                ? (
                  <HeroImage
                    key={ 'media_' + item.src }
                    src={item.src}
                    name={item.name}
                    width={item.width}
                    height={item.height}
                    onPrepare={this.handleSlidePrepare}
                    onLoad={this.handleSlideLoad}
                    mediaClassNames={imgClassNames} 
                  />
                )
                :(
                  <HeroVideo
                    key={ 'media_' + item.videoSrc.link }
                    videoSrc={item.videoSrc.link}
                    posterSrc={item.videoPosterSrc.link}
                    width={item.videoSrc.width}
                    height={item.videoSrc.height}
                    name={item.name}
                    active={isActive && this.props.active}
                    onPrepare={this.handleSlidePrepare}
                    onLoad={this.handleSlideLoad}
                    mediaClassNames={imgClassNames} 
                  />
                )
            )
            )}
          </div>
        </div>
      )
    })
  }

  handleHeroMouseEnter = () => {
    const { st } = this.props 

    if (st > 0) {
      return
    }

    // eslint-disable-next-line
    if (document.cookie.replace(/(?:(?:^|.*;\s*)hasScrollIconBeenSeen\s*\=\s*([^;]*).*$)|^.*$/, "$1") !== "true") {
      this.setState({
        icon: true
      })

      // Set cookie for 2 days 
      document.cookie = "hasScrollIconBeenSeen=true; max-age=172800"
    }
  }

  handleHeroMouseLeave = () => {
    this.setState({
      icon: false
    })
  }

  render() {
    const { preventScroll, primary, slides, active, inViewport, vw } = this.props
    const {
      index,
      icon,
      textInTransition,
      textOutTransition,
      spinnerHash,
      revealIntro } = this.state

    const heroClassNames = classnames({
      'hero--slides-ready': revealIntro,
      'hero--prevent-scroll': preventScroll,
      'hero--active': active,
      'hero--in-viewport': inViewport && !preventScroll,
      'hero--logo-active': false // active && primary && logoActive
    }, 'hero')

    const textClassNames =  classnames({
      'hero__text--transition-in': textInTransition,
      'hero__text--transition-out': textOutTransition
    }, 'hero__text')

    let orderedSlides = slides && slides.slice(slides.size - index)
    let limitedSlides = null
    let currentTitle = ''
    let currentSubtitle = ''

    if (orderedSlides) {
      orderedSlides = orderedSlides.concat(slides.slice(0, slides.size - index))
      limitedSlides = orderedSlides.slice(-2)
      currentTitle = limitedSlides.get(1).title
      currentSubtitle = limitedSlides.get(1).subtitle
    }

    return (
      <div className={heroClassNames}>
        <div 
          onMouseEnter={this.handleHeroMouseEnter}
          onMouseLeave={this.handleHeroMouseLeave} 
          className="hero__content" style={{ height: this.props.vh || '100%' }}>
          {limitedSlides && this.renderSlides(limitedSlides, vw)}
          <div className={textClassNames}>
            <div dangerouslySetInnerHTML={{ __html: currentTitle }} className="hero__title"></div>
            <div dangerouslySetInnerHTML={{ __html: currentSubtitle }} className="hero__subtitle"></div>
          </div>
          {primary && <HeroSpinner preventScroll={preventScroll} index={index} forceUpdateHash={spinnerHash}/>}
          {primary && <HeroIntro vw={this.props.vw} vh={this.props.vh} canStart={this.state.revealIntro} />}
          {primary && <HeroTooltip icon={icon} />}
        </div>
      </div>
    )
  }
}

export default Hero



// WEBPACK FOOTER //
// ./src/components/hero/hero.js